import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useModal } from '../contexts/ModalContext';
import WalletCard from '../components/WalletCard';
import WithdrawModal from '../components/modals/WithdrawModal';
import AmbassadorCard from '../components/AmbassadorCard';
import { usePartnerStatus } from '../hooks/usePartnerStatus';
import SubscriptionStatus from '../components/SubscriptionStatus';
import { DashboardHeader } from '../components/dna/DashboardHeader';
import { getStatusLabel, canShareRelease } from '../constants/forms';
import { authFetch } from '../services/api';
import { handleImageError } from '../utils/imageUtils';
import { coverGradientFor } from '../utils/coverGradient';

const Dashboard = () => {
  const { toggleMobileMenu } = useOutletContext();

  const {
    data,
    error,
    openCreateRelease,
    openEditRelease,
    openViewRelease,
    handleDeleteRelease,
    creationProgress,
    revenueData,
    revenueLoading,
    loadRevenueData,
    handleUpdateProfile,
  } = useDashboardContext();

  const { partnerStatus: partnerData, loading: partnerLoading, refresh: refreshPartner } = usePartnerStatus();
  const { confirmDelete } = useModal();
  const [shareMessage, setShareMessage] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);

  const [quota, setQuota] = useState(null);
  const [celebration, setCelebration] = useState(null);
  // Nombre de sorties avant chaque upload : sert à détecter LA première sortie
  const albumsCountRef = useRef((data?.albums || []).length);
  useEffect(() => { albumsCountRef.current = (data?.albums || []).length; }, [data?.albums]);
  const artistKeyRef = useRef(data?.artist?.slug || data?.artist?.id || 'me');
  useEffect(() => { artistKeyRef.current = data?.artist?.slug || data?.artist?.id || 'me'; }, [data?.artist]);
  const loadQuota = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions/status');
      if (res.ok) {
        const d = await res.json();
        if (d.system_enabled) setQuota(d);
      }
    } catch { /* silence */ }
  }, []);
  useEffect(() => { loadQuota(); }, [loadQuota]);

  // Célébration de la toute première sortie (une seule fois par artiste)
  useEffect(() => {
    const onUploadSuccess = (e) => {
      const album = e?.detail?.album;
      if (!album) return;
      if (albumsCountRef.current !== 0) return;
      const flag = `passio-first-release-celebrated-${artistKeyRef.current}`;
      try {
        if (localStorage.getItem(flag)) return;
        localStorage.setItem(flag, '1');
      } catch { /* stockage indisponible : on affiche quand même */ }
      // Petit délai : laisse le toast + le refresh passer d'abord
      setTimeout(() => setCelebration(album), 1200);
    };
    window.addEventListener('passio-upload-success', onUploadSuccess);
    return () => window.removeEventListener('passio-upload-success', onUploadSuccess);
  }, []);

  const isFree = data?.artist && data.artist.subscription_tier !== 'premium';
  const tracksExhausted = quota && (quota.tracks_remaining ?? 0) <= 0;
  const quotaTooltip = tracksExhausted && quota
    ? `Quota mensuel atteint (${quota.tracks_used_this_month || 0}/${quota.limit_this_month || 3})`
    : '';

  const onDeleteRelease = async (album) => {
    await confirmDelete({ itemName: album.title, onConfirm: async () => { await handleDeleteRelease(album); } });
  };

  const handleShare = async (album) => {
    const artistSlug = data?.artist?.slug;
    const albumSlug = album?.slug;
    if (!artistSlug || !albumSlug) return;
    const baseUrl = album?.is_free ? 'https://player.passiio.shop' : 'https://passiio.shop';
    try {
      await navigator.clipboard.writeText(`${baseUrl}/p/${artistSlug}/${albumSlug}`);
      setShareMessage('Lien copié !');
      setTimeout(() => setShareMessage(''), 3000);
    } catch { /* silence */ }
  };

  const firstName = data?.artist?.name?.split(' ')[0] || 'Artiste';
  // Carte catalogue : 3 sorties max — les uploads en cours d'abord (visibles de suite),
  // le reste via « Voir toutes les sorties »
  const RECENT_LIMIT = 3;
  const allAlbums = data?.albums || [];
  const recent = [
    ...allAlbums.filter((a) => a.isOptimistic),
    ...allAlbums.filter((a) => !a.isOptimistic),
  ].slice(0, RECENT_LIMIT);
  const used = quota?.tracks_used_this_month || 0;
  const limit = quota?.limit_this_month || (quota?.tier === 'premium' ? 30 : 3);

  return (
    <>
      <DashboardHeader
        title="Vue d’ensemble"
        subtitle="Voici l’état de ton projet aujourd’hui."
        onMenu={toggleMobileMenu}
      />
      <div className="dashboard-content">
      <div className="dashboard-intro-mobile anim-rise">
        <span>Bonjour, {firstName} <span className="wave-hand">👋</span></span>
        <small>{isFree ? 'Voici l’état de ton projet aujourd’hui.' : 'Tes avantages Premium sont actifs — à toi de jouer ! ✨'}</small>
      </div>

      {partnerData?.is_partner && (
        <section className="dashboard-ambassador anim-rise anim-d2" style={{ marginBottom: 16 }}>
          <AmbassadorCard partnerStatus={partnerData} loading={partnerLoading} onRefresh={refreshPartner} />
        </section>
      )}

      <div className="dashboard-main-grid">
        <section className="catalog-card anim-rise anim-d3">
          <div className="section-title-row">
            <div>
              <span className="content-kicker">Catalogue</span>
              <h2>Dernières sorties</h2>
              <p>Gérez vos publications récentes</p>
            </div>
            <Link to="/dashboard/releases" className="view-all">Voir toutes les sorties <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} /></Link>
          </div>

          {error && <div className="auth-error" style={{ margin: '0 0 16px' }}>{error}</div>}

          <div className="release-list">
            {recent.length === 0 ? (
              <div className="community-placeholder">
                <span className="content-kicker">Catalogue vide</span>
                <h2>Aucune sortie pour le moment.</h2>
                <p>Crée ta première sortie pour apparaître ici.</p>
                <button className="button button-dark small-button" onClick={openCreateRelease}>
                  Créer une sortie <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} />
                </button>
              </div>
            ) : recent.map((album) => (
              <article className="release-row" key={album.id} onClick={() => openViewRelease(album)}>
                <span className="release-row-cover" style={album.cover_url ? undefined : { background: coverGradientFor(album.id) }}>
                  {album.cover_url ? (
                    <img src={album.cover_url} alt={album.title} onError={handleImageError} />
                  ) : (
                    <span aria-hidden="true">{(album.title || 'P')[0].toUpperCase()}</span>
                  )}
                </span>
                <span className="release-row-main">
                  <b>{album.title}</b>
                  <small>
                    <span className="release-type">{album.type === 'single' ? 'Single' : 'Album'}</span>
                    <span className={`status status-${String(album.status || 'draft')}`}>{getStatusLabel(album.status)}</span>
                  </small>
                </span>
                <span className="release-row-meta">
                  <b>{Number(album.price_ariary) !== 0 ? `${Number(album.price_ariary || 0).toLocaleString()} Ar` : 'Gratuit'}</b>
                  {creationProgress[album.id] ? <span>{creationProgress[album.id]}%</span> : null}
                </span>
                <span className="release-row-actions" onClick={(e) => e.stopPropagation()}>
                  {canShareRelease(album.status) && (
                    <button title="Partager" onClick={() => handleShare(album)}>
                      <i className="fa-solid fa-share-nodes" />
                    </button>
                  )}
                  <button title="Modifier" onClick={() => openEditRelease(album)}>
                    <i className="fa-solid fa-pen" />
                  </button>
                  <button title="Supprimer" className="danger" onClick={() => onDeleteRelease(album)}>
                    <i className="fa-solid fa-trash" />
                  </button>
                </span>
              </article>
            ))}
          </div>
        </section>

        <aside className="dashboard-side">
          {!revenueLoading && revenueData && (
            <WalletCard
              balance={revenueData.net_revenue || 0}
              pendingBalance={revenueData.pending_balance || 0}
              totalPaid={revenueData.total_paid || 0}
              onWithdraw={() => setShowWithdraw(true)}
            />
          )}
          <button
            className="new-release-cta"
            onClick={tracksExhausted ? undefined : openCreateRelease}
            disabled={tracksExhausted}
            title={quotaTooltip}
            style={tracksExhausted ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            <span className="new-release-plus"><i className="fa-solid fa-plus" /></span>
            <span className="new-release-text">
              <b>Nouvelle sortie</b>
              <small>Publier un single ou un album</small>
            </span>
            <i className="fa-solid fa-arrow-right new-release-arrow" />
          </button>
        </aside>
      </div>

      <div className="dashboard-lower-grid">
        <div className="subscription-card">
          <SubscriptionStatus />
          {!quota ? null : (
            <p style={{ marginTop: 8, fontSize: '.75rem', color: 'var(--ink-soft)' }}>
              Quota : <strong>{used} / {limit}</strong> tracks ce mois.
            </p>
          )}
          {isFree && (
            <Link to="/dashboard/premium" className="sub-link">Débloquer avec Premium <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} /></Link>
          )}
        </div>
        <div className="analytics-teaser">
          <div>
            <span className="content-kicker">Performance</span>
            <h3>Analytics avancés</h3>
            <p>Visualisez vos tendances de ventes et d’écoutes.</p>
          </div>
          <div className="mini-chart"><span /><span /><span /><span /><span /><span /><span /></div>
          <Link to={isFree ? '/dashboard/premium' : '/dashboard/analytics'} className="button button-dark small-button">
            {isFree ? 'Débloquer avec Premium' : 'Découvrir'} <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} />
          </Link>
        </div>
      </div>

      {shareMessage && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#189b69', color: '#fff', padding: '10px 20px', borderRadius: 8, zIndex: 1000 }}>
          {shareMessage}
        </div>
      )}

      {/* Célébration de la première sortie : mêmes composants/styles que le retrait */}
      {celebration && (
        <div className="dna-modal-backdrop" role="presentation" onClick={() => setCelebration(null)}>
          <div className="dna-modal dna-withdraw" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="dna-withdraw-success">
              <div className="locked-icon"><i className="fa-solid fa-trophy" /></div>
              <span className="content-kicker">Première sortie en ligne</span>
              <h2>Félicitations, {firstName} !</h2>
              <p>« {celebration.title || 'Ta release'} » est en ligne. Partage ton lien et fais découvrir ta musique au monde.</p>
              {celebration.slug && (
                <button type="button" className="button button-crimson" onClick={() => handleShare(celebration)} style={{ width: '100%' }}>
                  Copier mon lien <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} />
                </button>
              )}
              <Link to="/dashboard/releases" className="button button-outline" onClick={() => setCelebration(null)} style={{ width: '100%', marginTop: 10 }}>
                Voir mes sorties
              </Link>
            </div>
          </div>
        </div>
      )}

      <WithdrawModal
        show={showWithdraw}
        onClose={() => setShowWithdraw(false)}
        netRevenue={revenueData?.net_revenue || 0}
        totalPaid={revenueData?.total_paid || 0}
        mvolaNumber={data?.artist?.mvola_number || ''}
        onSaveNumber={async (num) => handleUpdateProfile({ mvola_number: num })}
        onSuccess={() => { if (loadRevenueData) loadRevenueData(); }}
      />
      </div>
    </>
  );
};

export default Dashboard;
